import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andWhere,
  buildQuery,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import BusinessPartnerSubGroupAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner-sub-group/business-partner-sub-group.entity";
import { ICreateBusinessPartnerSubGroup } from "./interfaces/create-business-sub-group.interface";
import { IUpdateBusinessPartnerSubGroup } from "./interfaces/update-business-sub-group.interface";
import BusinessPartnerGroupAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner-group/business-partner-group.entity";
import ShortBusinessPartnerGroupAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner-group/business-partner-group-short/business-partner-group.short.entity";
import { IFilters } from "../../filters/interfaces/filterInterface";
const apiUrl = env.API_BUSINESS_PARTNER;
const moduleName = "business_partner_subgroup";

@Injectable({
  providedIn: "root",
})
export class BusinessPartnerSubGroupService extends AbstractService<
  BusinessPartnerSubGroupAttributes,
  ICreateBusinessPartnerSubGroup,
  IUpdateBusinessPartnerSubGroup
> {
  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Obtiene subgrupos de socios de negocio con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Dos objetos (OR lógico) - busca en 'id', 'name'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param filter Filters to apply
   * @returns Paginated data
   *
   * Search fields: id, name
   * Array filters: businessPartnerGroupId, status
   */
  findAllWithCount(
    options: IPagination<BusinessPartnerSubGroupAttributes>,
    filter: IFilters
  ): Observable<IPaginationResult<BusinessPartnerSubGroupAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de grupo, filtro de estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<BusinessPartnerSubGroupAttributes>[] => {
      const deletedFilter: FilterFn<BusinessPartnerSubGroupAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        filter?.businessPartnerGroupId && filter.businessPartnerGroupId.length > 0
          ? andWhere('group.id' as keyof BusinessPartnerSubGroupAttributes, Ops.in(...filter.businessPartnerGroupId))
          : noop(),
        filter?.status && filter.status.length > 0
          ? andWhere('statusId', Ops.in(...filter.status))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por ID (o solo condiciones si no hay búsqueda)
    const searchByIdQueryObject: FilterFn<BusinessPartnerSubGroupAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("id", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<BusinessPartnerSubGroupAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<BusinessPartnerSubGroupAttributes>(
      // Expandimos ambos objetos en el buildQuery
      ...searchByIdQueryObject,
      ...searchByNameQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<BusinessPartnerSubGroupAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(group?): Observable<IRequestResult<BusinessPartnerSubGroupAttributes[]>> {
    const query = buildQuery<BusinessPartnerSubGroupAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      group ? andWhere("group.id" as keyof ShortBusinessPartnerGroupAttributes , Ops.eq(group)) : noop(),
      select(["id", "name"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<BusinessPartnerSubGroupAttributes[]>>(API_URL);
  }
}
